import React, { useState, useEffect, useCallback, useMemo, memo, useRef, Suspense } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Alert,
  Tooltip,
  InputAdornment,
  LinearProgress,
    Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  FormControlLabel,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as DetailsIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as BudgetIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Sync as SyncIcon,
    SyncDisabled as SyncDisabledIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import * as api from '../../api/api';
import * as XLSX from 'xlsx';
import { formatCurrency, calculatePlanBreakEven, calculatePlanSavings, getDepartmentBudgetTotal } from './planningCalculations';
import { buttonStyles, formControlStyles, layoutStyles } from '../../styles/commonStyles';
import { ActionButton, BackButton, StyledFormControl, PageHeader, ElevatedCard, StyledTextField } from '../shared/StyledComponents';
// Enhanced lazy loading with better fallbacks
const OutcomeDetailsModal = React.lazy(() => import('./OutcomeDetailsModal'));
const OutcomeSelectionTable = React.lazy(() => import('./OutcomeSelectionTable'));
const EditableStatusChip = React.lazy(() => import('./EditableStatusChip'));
const RoleDetailsModal = React.lazy(() => import('./RoleDetailsModal'));
const OutcomeWizard = React.lazy(() => import('../Wizard/OutcomeWizard'));

// Loading fallback components
const ComponentSkeleton = ({ height = 40, width = '100%' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
    <Skeleton variant="rectangular" width={width} height={height} />
  </Box>
);

const ChipSkeleton = () => (
  <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 12 }} />
);

const OutcomeCardSkeleton = () => (
  <Card elevation={0} sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Skeleton width={24} height={20} sx={{ mb: 0.5 }} />
          <Skeleton width={24} height={20} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Skeleton width={200} height={20} />
            <ChipSkeleton />
            <ChipSkeleton />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {[...Array(3)].map((_, i) => (
            <Box key={i} sx={{ textAlign: 'right' }}>
              <Skeleton width={60} height={12} sx={{ mb: 0.5 }} />
              <Skeleton width={80} height={16} />
            </Box>
          ))}
          <Skeleton width={32} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const allowedStatuses = [
  "Planning", "Ready for Review", "Ready for ITGC", "Approved", "Complete", "Paused"
];

const planStatuses = [
  "Approved", "Under Consideration", "Declined"
];

// CAPEX threshold constant (should match the one in CostEstimationWizard)
const CAPEX_THRESHOLD = 500000;

// Utility functions moved outside component for better performance
const safeString = (value, defaultValue = '') => value ? String(value) : defaultValue;

// Constants moved outside component for better performance
const EMPTY_MONTHLY_ALLOCATIONS = new Array(12).fill(0);

// Helper function moved outside component for better performance
const getStatusColor = (status) => {
  switch (status) {
    case 'Approved':
      return 'success';
    case 'Rejected':
      return 'error';
    case 'Under Review':
      return 'warning';
    default:
      return 'default';
  }
};


// Memoized Outcome Card Component for better performance
const OutcomeCard = React.memo(({ 
  outcome, 
  index, 
  planStatus, 
  isDeclined, 
  capexOpexResult, 
  totalBudget, 
  originalCapexOpex,
  formData,
  getFilteredOrderedOutcomes,
  moveOutcome,
  handleOutcomeStatusChange,
  handlePlanStatusChange,
  handleCapexAdjustmentChange,
  handleOpexAdjustmentChange,
  handleSyncToggle,
  openDetailsModal,
  formatCurrency,
  allowedStatuses,
  planStatuses
}) => {
  // Memoize expensive style calculations
  const cardStyles = useMemo(() => ({
    mb: 1,
    opacity: isDeclined ? 0.6 : 1,
    border: '1px solid',
    borderColor: isDeclined ? 'error.main' : 'divider',
    borderRadius: 1,
    '&:hover': {
      borderColor: isDeclined ? 'error.main' : 'primary.main',
      bgcolor: 'action.hover'
    }
  }), [isDeclined]);

  return (
    <Card
      elevation={0}
      sx={cardStyles}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Move buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <IconButton
              size="small"
              onClick={() => moveOutcome(outcome.id, 'up')}
              disabled={index === 0}
              sx={{ p: 0.25 }}
            >
              <MoveUpIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveOutcome(outcome.id, 'down')}
              disabled={index === getFilteredOrderedOutcomes.length - 1}
              sx={{ p: 0.25 }}
            >
              <MoveDownIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
          </Box>
          
          {/* Outcome name and status */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography 
                variant="body1" 
                fontWeight={600}
                sx={{ 
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px'
                }}
              >
                {outcome.name}
              </Typography>
                                          <Suspense fallback={<ChipSkeleton />}>
                <EditableStatusChip
                  label="Outcome"
                  value={outcome.status}
                  options={allowedStatuses}
                  onChange={(newStatus) => handleOutcomeStatusChange(outcome.id, newStatus)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              </Suspense>
              <Suspense fallback={<ChipSkeleton />}>
                <EditableStatusChip
                  label="Plan"
                  value={planStatus}
                  options={planStatuses}
                  onChange={(newStatus) => handlePlanStatusChange(outcome.id, newStatus)}
                  color={
                    planStatus === "Approved" ? "success" :
                    planStatus === "Declined" ? "error" : "default"
                  }
                  variant="filled"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              </Suspense>
            </Box>
            
            {isDeclined && (
              <Typography 
                variant="caption" 
                color="error.main" 
                fontWeight={500}
                sx={{ fontSize: '0.7rem' }}
              >
                Declined - Excluded from totals
              </Typography>
            )}
          </Box>
          
          {/* Budget display */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Total Budget */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Total Budget
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight={600}
                color={isDeclined ? 'text.disabled' : 'primary.main'}
                sx={{ fontSize: '0.875rem' }}
              >
                {formatCurrency(totalBudget)}
              </Typography>
            </Box>
            
            {/* CAPEX */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
                CAPEX
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight={500}
                color={isDeclined ? 'text.disabled' : 'success.main'}
                sx={{ fontSize: '0.875rem' }}
              >
                {formatCurrency(capexOpexResult.capex)}
              </Typography>
            </Box>
            
            {/* OPEX */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem' }}>
                OPEX
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight={500}
                color={isDeclined ? 'text.disabled' : 'warning.main'}
                sx={{ fontSize: '0.875rem' }}
              >
                {formatCurrency(capexOpexResult.opex)}
              </Typography>
            </Box>
            
            {/* Details button */}
            <Tooltip title="View outcome details">
              <IconButton
                size="small"
                onClick={() => openDetailsModal(outcome)}
                sx={{ p: 0.5 }}
              >
                <DetailsIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for optimal re-rendering
  return (
    prevProps.outcome.id === nextProps.outcome.id &&
    prevProps.outcome.status === nextProps.outcome.status &&
    prevProps.planStatus === nextProps.planStatus &&
    prevProps.isDeclined === nextProps.isDeclined &&
    prevProps.totalBudget === nextProps.totalBudget &&
    JSON.stringify(prevProps.capexOpexResult) === JSON.stringify(nextProps.capexOpexResult) &&
    prevProps.index === nextProps.index &&
    prevProps.getFilteredOrderedOutcomes.length === nextProps.getFilteredOrderedOutcomes.length
  );
});

// Custom styled TextField for number inputs without spinner arrows
const NumberTextField = ({ sx, ...props }) => (
  <TextField
    {...props}
    sx={{
      '& input[type=number]': {
        '-moz-appearance': 'textfield',
      },
      '& input[type=number]::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      ...sx
    }}
  />
);

const CreateEditPlan = memo(function CreateEditPlan({ planId, onClose }) {
  const theme = useTheme();
  const isEditing = Boolean(planId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_budget: '',
    year: 2026,
    status: 'Draft', // Add plan status to form data
    selected_outcomes: [],
    outcome_adjustments: {},
    plan_statuses: {},
    outcome_order: [],
        // Add new fields for CAPEX/OPEX adjustments
    capex_adjustments: {},
    opex_adjustments: {},
    // Add sync settings for approved plans
    outcome_sync_settings: {}
  });

  const [availableOutcomes, setAvailableOutcomes] = useState([]);
  const [outcomeDetails, setOutcomeDetails] = useState({});
  const [itRoster, setItRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [outcomeSelectionExpanded, setOutcomeSelectionExpanded] = useState(false);
  const [planOutcomesExpanded, setPlanOutcomesExpanded] = useState(true);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedOutcomeForDetails, setSelectedOutcomeForDetails] = useState(null);
  const [roleDetailsModalOpen, setRoleDetailsModalOpen] = useState(false);
    const [planBreakEven, setPlanBreakEven] = useState(null);
  const [planSavings, setPlanSavings] = useState(null);
  const [colaRate, setCOLARate] = useState(4.5);
  
    // Remove unused performance monitoring state
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const [selectedOutcomeForFinancial, setSelectedOutcomeForFinancial] = useState(null);
  
    // Performance optimization refs
  const calculationTimeoutRef = useRef(null);
  const lastCalculationHashRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const processingTimeoutRef = useRef(null);
  
    // Enhanced caching with better cache management
  const outcomeDetailsCacheRef = useRef(new Map());
  const calculationCacheRef = useRef(new Map());
  
  // Add request deduplication cache
  const activeRequestsRef = useRef(new Map());
  
  // Add plan status filter state
  const [planStatusFilter, setPlanStatusFilter] = useState(['All']);
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  // Plan statuses for the plan itself
  const PLAN_STATUSES = [
    'Draft',
    'Under Review', 
    'Approved',
    'Rejected'
  ];

  // Fetch departments for dept filter
  useEffect(() => {
    api.getDepartments().then(res => {
      const depts = res?.departments || (Array.isArray(res) ? res : []);
      setDepartments(depts);
    }).catch(() => {});
  }, []);

  // Fetch COLA settings
  useEffect(() => {
    const fetchCOLASettings = async () => {
      try {
        const settings = await api.getCOLASettings();
        setCOLARate(settings.annual_cola_percentage || 4.5);
      } catch (error) {
        console.warn('COLA settings endpoint not available, using default rate:', error);
        setCOLARate(4.5); // Default fallback - don't show error to user
      }
    };
    fetchCOLASettings();
  }, []);

    // Memoized calculation functions for better performance
  const calculateOutcomeBudget = useCallback((outcomeId, useAdjustment = true) => {
    console.log(`[CreateEditPlan] calculateOutcomeBudget called for outcome ${outcomeId}, useAdjustment: ${useAdjustment}`);
    
    // Check if outcome is declined in the plan
    const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
    console.log(`[CreateEditPlan] Outcome ${outcomeId} plan status: ${planStatus}`);
    
    if (planStatus === "Declined") {
      console.log(`[CreateEditPlan] Outcome ${outcomeId} is declined, returning 0`);
      return 0;
    }

    // Use manual budget adjustment if available and requested
    if (useAdjustment && formData.outcome_adjustments[outcomeId] !== undefined) {
      const adjustedValue = formData.outcome_adjustments[outcomeId];
      console.log(`[CreateEditPlan] Using manual adjustment for outcome ${outcomeId}: ${adjustedValue}`);
      return adjustedValue;
    }

    // Calculate from outcome details
    const details = outcomeDetails[outcomeId];
    if (!details || !details.costEstimation || !details.costEstimation.roles) {
      console.log(`[CreateEditPlan] No cost estimation details for outcome ${outcomeId}`);
      return 0;
    }

    const costEstimation = details.costEstimation;
    const baseRoleCost = costEstimation.roles.reduce((sum, role) => {
      const months = (role.dateRanges || []).reduce((m, dr) =>
        m + ((dr.endMonth || costEstimation.adjustedMonths || 6) - (dr.startMonth || 1) + 1) * ((dr.allocation || 100) / 100), 0
      );
      return sum + (role.rate || 0) * months;
    }, 0);

    const totalFixedCost = (costEstimation.fixedCosts || []).reduce((sum, cost) => {
      return sum + (cost.monthlyCost || 0) * (cost.duration || costEstimation.adjustedMonths || 6);
    }, 0);

    const calculatedBudget = Math.round(baseRoleCost + totalFixedCost);
    console.log(`[CreateEditPlan] Calculated budget for outcome ${outcomeId}: ${calculatedBudget} (baseRoleCost: ${baseRoleCost}, totalFixedCost: ${totalFixedCost})`);
    
    return calculatedBudget;
  }, [outcomeDetails, formData.outcome_adjustments, formData.plan_statuses]);

        // Placeholder - calculateTotalBudget will be defined after calculateOutcomeCapexOpex

          // Progressive data loading with critical path prioritization
  const fetchData = useCallback(async () => {
    try {
      // Phase 1: Critical data only (outcomes for immediate UI)
      const outcomesResponse = await (async () => {
        try {
          const response = await api.getOutcomes();
          const outcomes = response.outcomes || response || [];
          return outcomes;
        } catch (err) {
          console.warn('CreateEditPlan: Failed to load outcomes:', err);
          return [];
        }
      })();
      
      // Process outcomes immediately to unblock UI
      const filteredOutcomes = outcomesResponse.filter(outcome => outcome.deleted !== true);
      setAvailableOutcomes(filteredOutcomes);
      
      // Auto-select "Ready for ITGC" outcomes for new plans (immediate)
      if (!isEditing && filteredOutcomes.length > 0) {
        const readyForITGCOutcomes = filteredOutcomes
          .filter(outcome => outcome.status === "Ready for ITGC")
          .map(outcome => outcome.id);
        
        if (readyForITGCOutcomes.length > 0) {
          const newPlanStatuses = {};
          const newOutcomeAdjustments = {};
          readyForITGCOutcomes.forEach(id => {
            newPlanStatuses[id] = "Under Consideration";
          });
          
          setFormData(prev => ({
            ...prev,
            selected_outcomes: readyForITGCOutcomes,
            outcome_order: readyForITGCOutcomes,
            plan_statuses: newPlanStatuses,
            outcome_adjustments: newOutcomeAdjustments
          }));
        }
      }
      
      // Phase 2: Non-critical data in background (after UI is interactive)
      setTimeout(async () => {
        try {
          const rosterResponse = await api.getITRoster();
          setItRoster(rosterResponse);
        } catch (err) {
          console.warn('Failed to load IT roster:', err);
          setItRoster([]);
        }
      }, 200); // Increased delay to ensure UI is fully rendered
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    }
  }, [isEditing]);

    // OPTIMIZED plan fetching using summary endpoint
  const fetchPlan = useCallback(async () => {
    try {
      setLoading(true);
      
            // Use standard plan endpoint
      const plan = await api.getPlan(planId);
      
      // Ensure plan_statuses exists and has default values for all selected outcomes
      const planStatuses = plan.plan_statuses || {};
      const selectedOutcomes = plan.selected_outcomes || [];
      
      // Set default status for outcomes that don't have a plan status
      selectedOutcomes.forEach(outcomeId => {
        if (!planStatuses[outcomeId]) {
          planStatuses[outcomeId] = "Under Consideration";
        }
      });
      
                  // Loading existing plan data
      console.log(`[CreateEditPlan] Loading plan data:`, plan);
      console.log(`[CreateEditPlan] Selected outcomes:`, selectedOutcomes);
      console.log(`[CreateEditPlan] Plan statuses:`, planStatuses);
      console.log(`[CreateEditPlan] Outcome adjustments:`, plan.outcome_adjustments);
      console.log(`[CreateEditPlan] CAPEX adjustments:`, plan.capex_adjustments);
      console.log(`[CreateEditPlan] OPEX adjustments:`, plan.opex_adjustments);
      
      const loadedFormData = {
        name: plan.name || '',
        description: plan.description || '',
        target_budget: plan.target_budget || '',
        year: plan.year || 2026,
        status: plan.status || 'Draft', // Load plan status
        selected_outcomes: selectedOutcomes,
        outcome_adjustments: plan.outcome_adjustments || {},
        plan_statuses: planStatuses, // Use the corrected plan statuses
        outcome_order: plan.outcome_order || selectedOutcomes,
        // Load the new CAPEX/OPEX adjustment fields
        capex_adjustments: plan.capex_adjustments || {},
        opex_adjustments: plan.opex_adjustments || {},
        // Load sync settings
        outcome_sync_settings: plan.outcome_sync_settings || {}
      };
      
      console.log(`[CreateEditPlan] Setting loaded form data:`, loadedFormData);
      setFormData(loadedFormData);
      
            // Note: Pre-calculated metrics removed as optimized endpoint doesn't exist
      
    } catch (error) {
      console.error('Error fetching plan:', error);
      setError('Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [planId]);

        // OPTIMIZED outcome details fetching with enhanced caching
  const fetchOutcomeDetails = useCallback(async () => {
    if (formData.selected_outcomes.length === 0) {
      setOutcomeDetails({});
      return;
    }
    
    // Create a hash to avoid refetching the same data
    const outcomeHash = formData.selected_outcomes.sort().join(',');
    if (lastCalculationHashRef.current === outcomeHash) {
      return;
    }
    
    // Check cache first
    const cachedData = outcomeDetailsCacheRef.current.get(outcomeHash);
    if (cachedData) {
      setOutcomeDetails(cachedData);
      lastCalculationHashRef.current = outcomeHash;
      return;
    }
    
    lastCalculationHashRef.current = outcomeHash;
    
        try {
      const startTime = performance.now();
      
                  // Use individual API calls for outcome details
      const outcomeDetailsMap = {};
      
            // Process outcomes in optimized batches
      const batchSize = 8; // Increased batch size for better throughput
      for (let i = 0; i < formData.selected_outcomes.length; i += batchSize) {
        const batch = formData.selected_outcomes.slice(i, i + batchSize);
        
                await Promise.all(batch.map(async (outcomeId) => {
          try {
            // Check if request is already in progress
            const requestKey = `${outcomeId}_details`;
            if (activeRequestsRef.current.has(requestKey)) {
              const existingRequest = activeRequestsRef.current.get(requestKey);
              const result = await existingRequest;
              outcomeDetailsMap[outcomeId] = result;
              return;
            }
            
            // Create new request and cache it
            const requestPromise = Promise.all([
              api.getCostEstimation(outcomeId).catch(() => null),
              api.getFinancialModel(outcomeId).catch(() => null)
            ]).then(([costEstimation, financialModel]) => ({
              costEstimation: costEstimation?.data || costEstimation,
              financialModel: financialModel?.data || financialModel
            })).finally(() => {
              activeRequestsRef.current.delete(requestKey);
            });
            
            activeRequestsRef.current.set(requestKey, requestPromise);
            outcomeDetailsMap[outcomeId] = await requestPromise;
          } catch (error) {
            outcomeDetailsMap[outcomeId] = {
              costEstimation: null,
              financialModel: null
            };
          }
        }));
        
        // Reduced delay between batches
        if (i + batchSize < formData.selected_outcomes.length) {
          await new Promise(resolve => setTimeout(resolve, 5));
        }
      }
      
      setOutcomeDetails(outcomeDetailsMap);
      
      // Cache the results
      outcomeDetailsCacheRef.current.set(outcomeHash, outcomeDetailsMap);
      
      // Limit cache size to prevent memory leaks
      if (outcomeDetailsCacheRef.current.size > 10) {
        const firstKey = outcomeDetailsCacheRef.current.keys().next().value;
        outcomeDetailsCacheRef.current.delete(firstKey);
      }
    } catch (error) {
      console.error('Error fetching outcome details:', error);
      setOutcomeDetails({});
    }
  }, [formData.selected_outcomes]);

      // New function to calculate individual outcome CAPEX/OPEX with approved budget priority
  const calculateOutcomeCapexOpex = useCallback((outcomeId, useAdjustment = true) => {
    console.log(`[CreateEditPlan] calculateOutcomeCapexOpex called for outcome ${outcomeId}, useAdjustment: ${useAdjustment}`);
    
    const details = outcomeDetails[outcomeId];
    const outcome = availableOutcomes.find(o => o.id === outcomeId);
    
    // Skip declined outcomes
    const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
    console.log(`[CreateEditPlan] CAPEX/OPEX calculation - Outcome ${outcomeId} plan status: ${planStatus}`);
    
    if (planStatus === "Declined") {
      console.log(`[CreateEditPlan] Outcome ${outcomeId} is declined, returning 0 CAPEX/OPEX`);
      return { 
        capex: 0, 
        opex: 0, 
        source: 'declined',
        sourceLabel: 'Declined'
      };
    }

        // Priority 1: Check for approved budget from outcome
    if (outcome?.approved_budget && 
        (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
      const approvedCapex = Math.round(outcome.approved_budget.capex || 0);
      const approvedOpex = Math.round(outcome.approved_budget.opex || 0);
      console.log(`[CreateEditPlan] Using approved budget for outcome ${outcomeId}: CAPEX ${approvedCapex}, OPEX ${approvedOpex}`);
      
      return {
        capex: approvedCapex,
        opex: approvedOpex,
        source: 'approved',
        sourceLabel: 'Approved'
      };
    }

        // Priority 2: Check for manual CAPEX/OPEX adjustments in plan
    if (useAdjustment && 
        (formData.capex_adjustments[outcomeId] !== undefined || 
         formData.opex_adjustments[outcomeId] !== undefined)) {
      
      console.log(`[CreateEditPlan] Found manual CAPEX/OPEX adjustments for outcome ${outcomeId}`);
      console.log(`[CreateEditPlan] CAPEX adjustment: ${formData.capex_adjustments[outcomeId]}`);
      console.log(`[CreateEditPlan] OPEX adjustment: ${formData.opex_adjustments[outcomeId]}`);
      
      const originalCapexOpex = calculateOutcomeCapexOpex(outcomeId, false);
      
      const adjustedCapex = formData.capex_adjustments[outcomeId] !== undefined ? 
                            formData.capex_adjustments[outcomeId] : originalCapexOpex.capex;
      const adjustedOpex = formData.opex_adjustments[outcomeId] !== undefined ? 
                           formData.opex_adjustments[outcomeId] : originalCapexOpex.opex;
      
      console.log(`[CreateEditPlan] Using adjusted values for outcome ${outcomeId}: CAPEX ${adjustedCapex}, OPEX ${adjustedOpex}`);
      
      return {
        capex: adjustedCapex,
        opex: adjustedOpex,
        source: 'adjusted',
        sourceLabel: 'Adjusted'
      };
    }

        // Priority 3: Calculate from cost estimation
    if (!details || !details.costEstimation) {
      console.log(`[CreateEditPlan] No cost estimation details for outcome ${outcomeId}`);
      return { 
        capex: 0, 
        opex: 0, 
        source: 'missing',
        sourceLabel: 'No Data'
      };
    }

    const costEstimation = details.costEstimation;
    
    // Get the base budget first
    const baseBudget = calculateOutcomeBudget(outcomeId, false);

    // Reuse baseBudget when no manual adjustment exists (avoids a redundant full recalculation)
    const adjustedBudget = (useAdjustment && formData.outcome_adjustments[outcomeId] !== undefined)
      ? formData.outcome_adjustments[outcomeId]
      : baseBudget;
    
    // Calculate adjustment ratio
    const adjustmentRatio = baseBudget > 0 ? adjustedBudget / baseBudget : 1;

            // Use stored CAPEX/OPEX amounts if available AND they are non-zero
    if (costEstimation.capexAmount !== undefined && costEstimation.opexAmount !== undefined &&
        (costEstimation.capexAmount > 0 || costEstimation.opexAmount > 0)) {
      const storedCapex = Math.round(costEstimation.capexAmount * adjustmentRatio);
      const storedOpex = Math.round(costEstimation.opexAmount * adjustmentRatio);
      console.log(`[CreateEditPlan] Using stored CAPEX/OPEX for outcome ${outcomeId}: CAPEX ${storedCapex}, OPEX ${storedOpex} (adjustment ratio: ${adjustmentRatio})`);
      
      return {
        capex: storedCapex,
        opex: storedOpex,
        source: 'estimated',
        sourceLabel: 'Estimated'
      };
    }

    // Fallback: Calculate from roles and fixed costs
    const roles = costEstimation.roles || [];
    const fixedCosts = costEstimation.fixedCosts || [];
    const adjustedMonths = costEstimation.adjustedMonths || 6;
    
    const baseRoleCost = roles.reduce((sum, role) => {
      const months = (role.dateRanges || []).reduce((m, dr) => {
        const startMonth = dr.startMonth || 1;
        const endMonth = dr.endMonth || adjustedMonths;
        const allocation = (dr.allocation || 100) / 100;
        return m + ((endMonth - startMonth + 1) * allocation);
      }, 0);
      return sum + (role.rate || 0) * months;
    }, 0);

    const totalFixedCost = fixedCosts.reduce((sum, cost) => {
      const duration = cost.duration || adjustedMonths;
      return sum + (cost.monthlyCost || 0) * duration;
    }, 0);

    const totalCost = baseRoleCost + totalFixedCost;

    // Apply CAPEX threshold logic (matching CostEstimationWizard)
    let capexAmount, opexAmount;
    
    if (totalCost < CAPEX_THRESHOLD && !costEstimation.capexOverride) {
      // Under threshold and no override - 100% OPEX
      capexAmount = 0;
      opexAmount = totalCost;
    } else {
      // Over threshold or override enabled - use ratio
      const capexRatio = costEstimation.capexRatio || 100;
      capexAmount = Math.round(baseRoleCost * (capexRatio / 100));
      opexAmount = Math.round(baseRoleCost * ((100 - capexRatio) / 100) + totalFixedCost);
    }

        const finalCapex = Math.round(capexAmount * adjustmentRatio);
    const finalOpex = Math.round(opexAmount * adjustmentRatio);
    
    console.log(`[CreateEditPlan] Calculated CAPEX/OPEX for outcome ${outcomeId}: CAPEX ${finalCapex}, OPEX ${finalOpex}`);
    console.log(`[CreateEditPlan] Calculation details - baseRoleCost: ${baseRoleCost}, totalFixedCost: ${totalFixedCost}, capexRatio: ${costEstimation.capexRatio || 100}%, adjustmentRatio: ${adjustmentRatio}`);
    
    return {
      capex: finalCapex,
      opex: finalOpex,
      source: 'estimated',
      sourceLabel: 'Estimated'
    };
  }, [outcomeDetails, availableOutcomes, formData.plan_statuses, formData.capex_adjustments, formData.opex_adjustments, calculateOutcomeBudget]);

  // Pre-compute CAPEX/OPEX for all selected outcomes once per dependency change.
  // All downstream memos and the render loop read from this map instead of calling
  // calculateOutcomeCapexOpex individually, eliminating redundant per-outcome recalculations.
  const outcomeCapexOpexMap = useMemo(() => {
    const map = {};
    for (const outcomeId of formData.selected_outcomes) {
      map[outcomeId] = {
        adjusted: calculateOutcomeCapexOpex(outcomeId, true),
        base: calculateOutcomeCapexOpex(outcomeId, false),
      };
    }
    return map;
  }, [formData.selected_outcomes, calculateOutcomeCapexOpex]);

      // Memoized total budget calculation with better performance
  const calculateTotalBudget = useMemo(() => {
    const total = formData.selected_outcomes.reduce((total, outcomeId) => {
      const result = outcomeCapexOpexMap[outcomeId]?.adjusted;
      if (!result) return total;
      return total + result.capex + result.opex;
    }, 0);
    return total;
  }, [formData.selected_outcomes, outcomeCapexOpexMap]);

      // Memoized outcomes lookup for better performance
  const outcomesLookup = useMemo(() => {
    return availableOutcomes.reduce((acc, outcome) => {
      acc[outcome.id] = outcome;
      return acc;
    }, {});
  }, [availableOutcomes]);
  
  // Updated function to calculate plan-level break-even from individual outcomes
  const calculatePlanBreakEvenFromOutcomes = useCallback(async () => {
    try {
      // Use the local calculation function instead of API
      const breakEvenData = calculatePlanBreakEven(
        formData.selected_outcomes,
        outcomeDetails,
        formData.plan_statuses,
        formData.outcome_adjustments,
        outcomesLookup
      );
      return breakEvenData;
    } catch (error) {
      console.warn('Break-even calculation failed:', error);
      return null;
    }
  }, [formData.selected_outcomes, formData.plan_statuses, formData.outcome_adjustments, outcomeDetails, outcomesLookup]);

      // Calculate plan savings using local calculation function
  const calculatePlanSavingsFromAPI = useCallback(async () => {
    try {
      // Use the local calculation function instead of API
      const savingsData = calculatePlanSavings(
        formData.selected_outcomes,
        outcomeDetails,
        formData.plan_statuses,
        formData.outcome_adjustments,
        colaRate,
        outcomesLookup
      );
      return savingsData;
    } catch (error) {
      console.warn('Savings calculation failed:', error);
      
      // Fallback calculation - simplified version
      let totalSavings = 0;
      let contributingOutcomes = 0;
      let totalOutcomes = 0;

      formData.selected_outcomes.forEach(outcomeId => {
        const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
        if (planStatus === "Declined") return;
        
        totalOutcomes++;
        const details = outcomeDetails[outcomeId];
        if (details?.financialModel) {
          const financialModel = details.financialModel;
          let annualSavings = 0;
          
          if (financialModel.summary?.efficiency_gains) {
            annualSavings = financialModel.summary.efficiency_gains;
          } else if (financialModel.roles && financialModel.milestones) {
            // Calculate from roles and efficiency gains
            (financialModel.roles || []).forEach(role => {
              let totalGainForRole = 0;
              (financialModel.milestones || []).forEach(milestone => {
                const efficiencyGainsData = milestone.efficiency_gains || {};
                const gainForRole = efficiencyGainsData[role.title] || 0;
                totalGainForRole += gainForRole;
              });
              const roleEfficiencyGain = Math.min(totalGainForRole, 100);
              
              const roleCost = (Number(role.count) || 0) * (Number(role.avg_salary) || 0) * 
                              (Number(role.time_percentage || 100) / 100);
              annualSavings += roleCost * roleEfficiencyGain / 100;
            });
          }

          if (annualSavings > 0) {
            totalSavings += annualSavings;
            contributingOutcomes++;
          }
        }
      });

      const totalBudget = calculateTotalBudget();
      const savingsPercentage = totalBudget > 0 ? (totalSavings / totalBudget) * 100 : 0;

      return {
        totalSavings,
        contributingOutcomes,
        totalOutcomes,
                savingsPercentage
      };
    }
  }, [formData.selected_outcomes, formData.plan_statuses, formData.outcome_adjustments, colaRate, outcomeDetails, calculateTotalBudget, outcomesLookup]);

        // Debounced calculation effects with enhanced caching
  useEffect(() => {
    console.log(`[CreateEditPlan] Calculation effect triggered`);
    console.log(`[CreateEditPlan] Selected outcomes: ${formData.selected_outcomes.length}`);
    console.log(`[CreateEditPlan] Outcome details keys: ${Object.keys(outcomeDetails).length}`);
    console.log(`[CreateEditPlan] Plan statuses:`, formData.plan_statuses);
    console.log(`[CreateEditPlan] Outcome adjustments:`, formData.outcome_adjustments);
    console.log(`[CreateEditPlan] CAPEX adjustments:`, formData.capex_adjustments);
    console.log(`[CreateEditPlan] OPEX adjustments:`, formData.opex_adjustments);
    
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }
    
        calculationTimeoutRef.current = setTimeout(() => {
      // Ensure we have outcome details for all selected outcomes before calculating
      const hasAllOutcomeDetails = formData.selected_outcomes.length > 0 && 
        formData.selected_outcomes.every(id => outcomeDetails[id]);
      
      if (hasAllOutcomeDetails) {
        // Create cache key for calculations
        const calcKey = `${formData.selected_outcomes.sort().join(',')}_${JSON.stringify(formData.plan_statuses)}_${JSON.stringify(formData.outcome_adjustments)}_${colaRate}`;
        console.log(`[CreateEditPlan] Calculation cache key: ${calcKey}`);
        
        // Check calculation cache
        const cachedCalc = calculationCacheRef.current.get(calcKey);
        if (cachedCalc) {
          console.log(`[CreateEditPlan] Using cached calculation results`);
          setPlanBreakEven(cachedCalc.breakEven);
          setPlanSavings(cachedCalc.savings);
          return;
        }
        
        console.log(`[CreateEditPlan] Calculating break-even and savings`);
        Promise.all([
          calculatePlanBreakEvenFromOutcomes(),
          calculatePlanSavingsFromAPI()
        ]).then(([breakEven, savings]) => {
          console.log(`[CreateEditPlan] Calculation results - Break-even:`, breakEven);
          console.log(`[CreateEditPlan] Calculation results - Savings:`, savings);
          
          setPlanBreakEven(breakEven);
          setPlanSavings(savings);
          
          // Cache the results
          calculationCacheRef.current.set(calcKey, { breakEven, savings });
          
          // Limit cache size
          if (calculationCacheRef.current.size > 5) {
            const firstKey = calculationCacheRef.current.keys().next().value;
            calculationCacheRef.current.delete(firstKey);
          }
        });
      } else {
        console.log(`[CreateEditPlan] Clearing break-even and savings (no outcomes or details)`);
        setPlanBreakEven(null);
        setPlanSavings(null);
      }
    }, 500); // Increased debounce time to ensure data consistency
    
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [formData.selected_outcomes, formData.plan_statuses, formData.outcome_adjustments, formData.capex_adjustments, formData.opex_adjustments, outcomeDetails, colaRate, calculatePlanBreakEvenFromOutcomes, calculatePlanSavingsFromAPI]);

          // Enhanced progressive data loading with better performance
  useEffect(() => {
    let isMounted = true;
    let loadingTimeout;
    
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Phase 1: Critical data first
        await fetchData();
        
        if (isEditing && isMounted) {
          await fetchPlan();
        }
        
        // Set loading to false early to show UI
        if (isMounted) {
          setLoading(false);
        }
        
      } catch (error) {
        console.error('Error loading initial data:', error);
        if (isMounted) {
          setError('Failed to load data');
          setLoading(false);
        }
      }
    };
    
    // Set maximum loading timeout
    loadingTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('CreateEditPlan: Loading timeout reached, forcing completion');
        setLoading(false);
      }
    }, 8000); // 8 second timeout
    
    fetchTimeoutRef.current = setTimeout(() => {
      loadData().finally(() => {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
      });
    }, 100);
    
    return () => {
      isMounted = false;
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [isEditing]);
  
        // Separate effect for outcome details with debouncing
  useEffect(() => {
    console.log(`[CreateEditPlan] Outcome details effect triggered with ${formData.selected_outcomes.length} selected outcomes`);
    
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    
    processingTimeoutRef.current = setTimeout(() => {
      if (formData.selected_outcomes.length > 0) {
        console.log(`[CreateEditPlan] Fetching outcome details for outcomes: ${formData.selected_outcomes.join(', ')}`);
        fetchOutcomeDetails();
      }
    }, 300); // Increased debounce to prevent race conditions
    
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, [formData.selected_outcomes, fetchOutcomeDetails]);

        // Sync plan CAPEX/OPEX values to outcome approved budget
  const syncOutcomeApprovedBudget = useCallback(async (outcomeId) => {
    try {
      const outcome = availableOutcomes.find(o => o.id === outcomeId);
      if (!outcome) return;

      // Get the final CAPEX/OPEX values from the plan
      const { capex, opex } = calculateOutcomeCapexOpex(outcomeId, true);
      
      // Update the outcome with the synced approved budget
      const updatedOutcome = {
        ...outcome,
        approved_budget: {
          capex: capex,
          opex: opex
        }
      };

      await api.updateOutcome(outcomeId, updatedOutcome);

      // Update local state
      setAvailableOutcomes(prev => 
        prev.map(o => o.id === outcomeId ? updatedOutcome : o)
      );

      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('outcomeApprovedBudgetSynced', { 
        detail: { outcomeId, capex, opex } 
      }));
    } catch (error) {
      console.error('Error syncing approved budget:', error);
      setError('Failed to sync approved budget');
    }
  }, [availableOutcomes, calculateOutcomeCapexOpex]);

  // Auto-sync approved budgets when plan CAPEX/OPEX adjustments change for synced outcomes
  useEffect(() => {
    if (formData.status === 'Approved') {
      const syncedOutcomes = formData.selected_outcomes.filter(outcomeId => {
        const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
        return formData.outcome_sync_settings[outcomeId] === true && planStatus === 'Approved';
      });
      
      if (syncedOutcomes.length > 0) {
        const timeoutId = setTimeout(() => {
          syncedOutcomes.forEach(outcomeId => {
            const outcome = availableOutcomes.find(o => o.id === outcomeId);
            if (outcome?.approved_budget && 
                (outcome.approved_budget.capex > 0 || outcome.approved_budget.opex > 0)) {
              syncOutcomeApprovedBudget(outcomeId);
            }
          });
        }, 500);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [formData.capex_adjustments, formData.opex_adjustments, formData.outcome_sync_settings, formData.status, formData.selected_outcomes, formData.plan_statuses, availableOutcomes, syncOutcomeApprovedBudget]);

      // Memoized calculation functions for better performance
        const calculateTotalCapexOpex = useMemo(() => {
    let totalCapex = 0;
    let totalOpex = 0;
    formData.selected_outcomes.forEach(outcomeId => {
      const result = outcomeCapexOpexMap[outcomeId]?.adjusted;
      if (result) {
        totalCapex += result.capex;
        totalOpex += result.opex;
      }
    });
    return { totalCapex, totalOpex };
  }, [formData.selected_outcomes, outcomeCapexOpexMap]);

  const calculateOutcomeStats = useMemo(() => {
    let totalOutcomes = 0;
    let opexOnlyOutcomes = 0;
    formData.selected_outcomes.forEach(outcomeId => {
      const entry = outcomeCapexOpexMap[outcomeId];
      if (entry && entry.adjusted.source !== 'declined') {
        totalOutcomes++;
        if (entry.base.capex === 0) {
          opexOnlyOutcomes++;
        }
      }
    });
    return { totalOutcomes, opexOnlyOutcomes };
  }, [formData.selected_outcomes, outcomeCapexOpexMap]);

  const getBudgetStatus = useMemo(() => {
    if (!formData.target_budget) return null;
    
    const totalBudget = calculateTotalBudget;
    const targetBudget = parseFloat(formData.target_budget) || 0;
    const difference = totalBudget - targetBudget;
    const isOver = difference > 0;
    const percentage = Math.abs((difference / targetBudget) * 100);
    
    return {
      isOver,
      difference: Math.abs(difference),
      percentage: Math.min(percentage, 100), // Cap at 100% for display
      color: isOver ? 'error' : 'success',
      text: isOver ? 'Over Budget' : 'Under Budget'
    };
  }, [formData.target_budget, calculateTotalBudget]);

      const handleOutcomeSelectionChange = useCallback((selectedIds) => {
    console.log(`[CreateEditPlan] handleOutcomeSelectionChange called with:`, selectedIds);
    console.log(`[CreateEditPlan] Previous selected outcomes:`, formData.selected_outcomes);
    
    const newPlanStatuses = { ...formData.plan_statuses };
    const newOutcomeAdjustments = { ...formData.outcome_adjustments };
    
    // Set default plan status for new outcomes
    selectedIds.forEach(id => {
      if (!newPlanStatuses[id]) {
        console.log(`[CreateEditPlan] Setting default plan status for new outcome ${id}`);
        newPlanStatuses[id] = "Under Consideration";
      }
      // Don't set default adjustments - let them be calculated from cost estimation
    });
    
    console.log(`[CreateEditPlan] Updated plan statuses:`, newPlanStatuses);
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        selected_outcomes: selectedIds,
        outcome_order: selectedIds,
        plan_statuses: newPlanStatuses,
        outcome_adjustments: newOutcomeAdjustments
      };
      console.log(`[CreateEditPlan] Setting new form data:`, newFormData);
      return newFormData;
    });
  }, [formData.plan_statuses, formData.outcome_adjustments, formData.selected_outcomes]);

  

  // Add new handlers for CAPEX/OPEX adjustments
    const handleCapexAdjustmentChange = useCallback((outcomeId, adjustmentValue) => {
    const numericValue = adjustmentValue === '' ? undefined : (parseFloat(adjustmentValue) || 0);
    setFormData(prev => {
      const newCapexAdj = { ...prev.capex_adjustments };
      if (numericValue === undefined) {
        delete newCapexAdj[outcomeId];
      } else {
        newCapexAdj[outcomeId] = numericValue;
      }
      return {
        ...prev,
        capex_adjustments: newCapexAdj
      };
    });
  }, []);

        const handleOpexAdjustmentChange = useCallback((outcomeId, adjustmentValue) => {
    const numericValue = adjustmentValue === '' ? undefined : (parseFloat(adjustmentValue) || 0);
    setFormData(prev => {
      const newOpexAdj = { ...prev.opex_adjustments };
      if (numericValue === undefined) {
        delete newOpexAdj[outcomeId];
      } else {
        newOpexAdj[outcomeId] = numericValue;
      }
      return {
        ...prev,
        opex_adjustments: newOpexAdj
      };
    });
  }, []);

    // Handle sync toggle for approved plans
  const handleSyncToggle = useCallback(async (outcomeId, enabled) => {
    setFormData(prev => ({
      ...prev,
      outcome_sync_settings: {
        ...prev.outcome_sync_settings,
        [outcomeId]: enabled
      }
    }));

    // If enabling sync, immediately sync the values
    if (enabled && formData.status === 'Approved') {
      await syncOutcomeApprovedBudget(outcomeId);
    }
  }, [formData.status, syncOutcomeApprovedBudget]);

  

    

        const handlePlanStatusChange = useCallback((outcomeId, status) => {
    console.log(`[CreateEditPlan] handlePlanStatusChange called for outcome ${outcomeId}, new status: ${status}`);
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        plan_statuses: {
          ...prev.plan_statuses,
          [outcomeId]: status
        }
      };
      console.log(`[CreateEditPlan] Updated plan statuses:`, newFormData.plan_statuses);
      return newFormData;
    });
  }, []);

    const handleOutcomeStatusChange = useCallback(async (outcomeId, newStatus) => {
    try {
      // Find the outcome in availableOutcomes
      const outcome = availableOutcomes.find(o => o.id === outcomeId);
      if (!outcome) return;

      // Update the outcome status
      const updatedOutcome = { ...outcome, status: newStatus };
      await api.updateOutcome(outcomeId, updatedOutcome);

      // Update local state
      setAvailableOutcomes(prev => 
        prev.map(o => o.id === outcomeId ? { ...o, status: newStatus } : o)
      );

      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('outcomeStatusUpdated', { 
        detail: { outcomeId, newStatus } 
      }));
    } catch (error) {
      console.error('Error updating outcome status:', error);
      setError('Failed to update outcome status');
    }
  }, [availableOutcomes]);

    // Replace drag-and-drop with simple move up/down buttons
  const moveOutcome = useCallback((outcomeId, direction) => {
    const currentIndex = formData.outcome_order.indexOf(outcomeId);
    if (currentIndex === -1) return;

    const newOrder = [...formData.outcome_order];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < newOrder.length) {
      // Swap elements
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
      
      setFormData(prev => ({
        ...prev,
        outcome_order: newOrder
      }));
    }
  }, [formData.outcome_order]);

  

    const openDetailsModal = useCallback((outcome) => {
    setSelectedOutcomeForDetails(outcome);
    setDetailsModalOpen(true);
  }, []);

    const handleModalPlanStatusChange = useCallback((newStatus) => {
    if (selectedOutcomeForDetails) {
      handlePlanStatusChange(selectedOutcomeForDetails.id, newStatus);
    }
  }, [selectedOutcomeForDetails, handlePlanStatusChange]);

    const handleModalOutcomeStatusChange = useCallback((newStatus) => {
    if (selectedOutcomeForDetails) {
      handleOutcomeStatusChange(selectedOutcomeForDetails.id, newStatus);
      // Update the selected outcome for the modal
      setSelectedOutcomeForDetails(prev => ({ ...prev, status: newStatus }));
    }
  }, [selectedOutcomeForDetails, handleOutcomeStatusChange]);

    const handleViewFinancialDetails = useCallback((outcomeId) => {
    const outcome = availableOutcomes.find(o => o.id === outcomeId);
    if (outcome) {
      setSelectedOutcomeForFinancial(outcome);
      setDetailsModalOpen(false); // Close the details modal
      setFinancialModalOpen(true); // Open the financial modal
    }
  }, [availableOutcomes]);

    const handleReturnToDetails = useCallback(() => {
    setFinancialModalOpen(false); // Close the financial modal
    setSelectedOutcomeForFinancial(null);
    setDetailsModalOpen(true); // Reopen the details modal
  }, []);

  // Handle plan approval/rejection
  const handlePlanStatusUpdate = async (newStatus) => {
    try {
      // Check if trying to approve and there's already an approved plan for this year
      if (newStatus === 'Approved') {
        // Fetch all plans to check for existing approved plans
        const response = await api.getPlans();
        const allPlans = response.plans || [];
        
        const existingApprovedPlan = allPlans.find(p => 
          p.year === formData.year && 
          p.status === 'Approved' && 
          p.id !== planId
        );

        if (existingApprovedPlan) {
          setError(`Cannot approve plan. Plan "${existingApprovedPlan.name}" is already approved for year ${formData.year}. Please change or delete the existing approved plan first.`);
          return;
        }
      }

      const updatedFormData = { ...formData, status: newStatus };
      setFormData(updatedFormData);

            // If approving, update outcome statuses to "Funded" for approved outcomes
      if (newStatus === 'Approved') {
        const approvedOutcomeIds = formData.selected_outcomes.filter(outcomeId => {
          const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
          return planStatus !== "Declined";
        });

                // Enable sync by default for outcomes with "Approved" plan status
        const newSyncSettings = { ...formData.outcome_sync_settings };
        approvedOutcomeIds.forEach(outcomeId => {
          const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
          if (newSyncSettings[outcomeId] === undefined && planStatus === 'Approved') {
            newSyncSettings[outcomeId] = true; // Default to sync enabled for approved outcomes
          }
        });
        
        setFormData(prev => ({
          ...prev,
          outcome_sync_settings: newSyncSettings
        }));

        // Update outcome statuses and approved budgets
        for (const outcomeId of approvedOutcomeIds) {
          try {
            const outcome = availableOutcomes.find(o => o.id === outcomeId);
            if (outcome) {
              // Get the final CAPEX/OPEX values from the plan
              const { capex, opex } = calculateOutcomeCapexOpex(outcomeId, true);
              
              // Update the outcome with approved budget and funded status
              await api.updateOutcome(outcomeId, { 
                ...outcome, 
                status: 'Funded',
                approved_budget: {
                  capex: capex,
                  opex: opex
                }
              });
            }
          } catch (error) {
            console.warn(`Failed to update outcome ${outcomeId}:`, error);
          }
        }

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('planApproved', {
          detail: { planId: planId, outcomeIds: approvedOutcomeIds }
        }));
      }
    } catch (error) {
      console.error('Error updating plan status:', error);
      setError('Failed to update plan status');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Plan name is required');
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Validate and clean the data before sending
      const planData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        target_budget: formData.target_budget ? parseFloat(formData.target_budget) : null,
        year: parseInt(formData.year) || 2026,
        status: formData.status || 'Draft', // Include plan status
        selected_outcomes: formData.selected_outcomes || [],
        outcome_adjustments: formData.outcome_adjustments || {},
        plan_statuses: formData.plan_statuses || {},
        outcome_order: formData.outcome_order || formData.selected_outcomes || [],
                total_budget: calculateTotalBudget,
        // Add the new CAPEX/OPEX adjustment fields
        capex_adjustments: formData.capex_adjustments || {},
        opex_adjustments: formData.opex_adjustments || {},
        // Add sync settings
        outcome_sync_settings: formData.outcome_sync_settings || {}
      };

      // Additional validation
      if (planData.selected_outcomes.length === 0) {
        setError('At least one outcome must be selected');
        return;
      }

      // Ensure outcome_order contains all selected outcomes
      const missingOutcomes = planData.selected_outcomes.filter(
        id => !planData.outcome_order.includes(id)
      );
      if (missingOutcomes.length > 0) {
        planData.outcome_order = [...planData.outcome_order, ...missingOutcomes];
      }

      // Ensure all selected outcomes have a plan status
      planData.selected_outcomes.forEach(outcomeId => {
        if (!planData.plan_statuses[outcomeId]) {
          planData.plan_statuses[outcomeId] = "Under Consideration";
        }
      });

      // Saving plan data

      let result;
      if (isEditing) {
        result = await api.updatePlan(planId, planData);
      } else {
        result = await api.createPlan(planData);
      }

            // Handle plan approval if status is being set to Approved
      if (planData.status === 'Approved' && formData.status !== 'Approved') {
        await handlePlanStatusUpdate('Approved');
      }

      // Plan saved successfully
      onClose();
    } catch (error) {
      console.error('Error saving plan:', error);
      
      // Enhanced error handling
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
                // Server response error logged
        
        if (status === 422) {
          // Validation error - show specific details
          if (data && data.detail) {
            if (Array.isArray(data.detail)) {
              // FastAPI validation errors
              const validationErrors = data.detail.map(err => 
                `${err.loc?.join('.')} - ${err.msg}`
              ).join(', ');
              setError(`Validation error: ${validationErrors}`);
            } else if (typeof data.detail === 'string') {
              setError(`Validation error: ${data.detail}`);
            } else {
              setError('Validation error: Please check your input data');
            }
          } else if (data && data.message) {
            setError(`Validation error: ${data.message}`);
          } else {
            setError('Validation error: The server rejected the data. Please check all required fields.');
          }
        } else if (status === 400) {
          setError('Bad request: Please check your input data');
        } else if (status === 500) {
          setError('Server error: Please try again later');
        } else {
          setError(`Server error (${status}): ${data?.message || 'Unknown error'}`);
        }
      } else if (error.request) {
        setError('Network error: Unable to reach the server');
      } else {
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

      // Memoized function to get filtered outcomes based on plan status
      const getFilteredOrderedOutcomes = useMemo(() => {
    // Use selected_outcomes as fallback if outcome_order is empty or missing
    const orderToUse = formData.outcome_order && formData.outcome_order.length > 0 
      ? formData.outcome_order 
      : formData.selected_outcomes;
    
    const orderedOutcomes = orderToUse
      .map(outcomeId => availableOutcomes.find(o => o.id === outcomeId))
      .filter(Boolean);

    if (planStatusFilter.includes('All')) {
      return orderedOutcomes;
    }

    return orderedOutcomes.filter(outcome => {
      const planStatus = formData.plan_statuses[outcome.id] || "Under Consideration";
      return planStatusFilter.includes(planStatus);
    });
  }, [formData.outcome_order, formData.selected_outcomes, availableOutcomes, planStatusFilter, formData.plan_statuses]);

    // Filter IT roster members for a specific role - memoized for better performance
  const getFilteredRosterForRole = useCallback((roleFunction) => {
    if (!roleFunction) return itRoster;
    
    return itRoster.filter(member => {
      if (member.function && member.function.toLowerCase() === roleFunction.toLowerCase()) {
        return true;
      }
      
      const functionCategories = {
        'developer': ['frontend developer', 'backend developer', 'fullstack developer', 'software engineer'],
        'design': ['ux design', 'ui design', 'designer'],
        'product': ['product manager', 'product owner'],
        'qa': ['quality assurance', 'tester'],
        'analyst': ['business analyst', 'data analyst'],
        'lead': ['tech lead', 'team lead', 'engineering manager']
      };
      
      for (const [category, functions] of Object.entries(functionCategories)) {
        const roleFunctionLower = roleFunction.toLowerCase();
        const memberFunctionLower = member.function ? member.function.toLowerCase() : '';
        
        if (
          (functions.some(f => roleFunctionLower.includes(f)) && 
           functions.some(f => memberFunctionLower.includes(f))) ||
          (roleFunctionLower.includes(category) && functions.some(f => memberFunctionLower.includes(f))) ||
          (memberFunctionLower.includes(category) && functions.some(f => roleFunctionLower.includes(f)))
        ) {
          return true;
        }
      }
      
      return false;
    });
  }, [itRoster]);

    // Memoized plan outcome counts function
  const getPlanOutcomeCounts = useMemo(() => {
    const filteredOutcomes = getFilteredOrderedOutcomes;
    const approvedCount = filteredOutcomes.filter(outcome => 
      formData.plan_statuses[outcome.id] === "Approved"
    ).length;
    const totalCount = filteredOutcomes.length;
    const allOutcomesCount = formData.selected_outcomes.length;
    return { approvedCount, totalCount, allOutcomesCount };
  }, [getFilteredOrderedOutcomes, formData.plan_statuses, formData.selected_outcomes]);

    const handlePlanStatusFilterChange = useCallback((event) => {
    const value = event.target.value;
    
    // Handle "All" selection
    if (value.includes('All')) {
      if (planStatusFilter.includes('All')) {
        // If "All" was already selected and clicked again, keep only "All"
        setPlanStatusFilter(['All']);
      } else {
        // If "All" is newly selected, select only "All"
        setPlanStatusFilter(['All']);
      }
    } else {
      // Remove "All" if other options are selected
      const newFilter = value.filter(v => v !== 'All');
      setPlanStatusFilter(newFilter.length > 0 ? newFilter : ['All']);
    }
  }, [planStatusFilter]);

    

    // Memoized Excel export data preparation
  const exportData = useMemo(() => {
    if (formData.selected_outcomes.length === 0) {
      return null;
    }

    const data = [];
    
    // Create header row
    const headers = [
      'Outcome Name', 'Outcome Status', 'Plan Status', 'Total Budget', 'CAPEX', 'OPEX', 
      'Budget Source', 'Company', 'Location', 'Function', 'Role', 'Rate', 'Allocation %', 
      'Start Month', 'End Month', 'Duration (Months)', 'Total Cost', 'CAPEX/OPEX Classification'
    ];
    
    data.push(headers);

    // Process each outcome in the plan
    const orderedOutcomes = formData.outcome_order && formData.outcome_order.length > 0 
      ? formData.outcome_order 
      : formData.selected_outcomes;
    
    orderedOutcomes.forEach(outcomeId => {
      const outcome = availableOutcomes.find(o => o.id === outcomeId);
      if (!outcome) return;
      
      const planStatus = formData.plan_statuses[outcomeId] || "Under Consideration";
      const capexOpexResult = calculateOutcomeCapexOpex(outcomeId, true);
      const { capex, opex } = capexOpexResult;
      const totalBudget = capex + opex;
      const details = outcomeDetails[outcomeId];
      
      // If outcome is declined, add a single summary row
      if (planStatus === "Declined") {
        const summaryRow = [
          outcome.name || '',
          outcome.status || '',
          planStatus,
          0, 0, 0,
          'Declined',
          '', '', '', '', '', '', '', '', '', '', ''
        ];
        data.push(summaryRow);
        return;
      }
      
      // Add outcome summary row first
      const summaryRow = [
        outcome.name || '',
        outcome.status || '',
        planStatus,
        totalBudget,
        capex,
        opex,
        capexOpexResult.sourceLabel,
        '', '', '', '', '', '', '', '', '', '', 'Summary'
      ];
      data.push(summaryRow);
      
      // Add detailed role breakdown if cost estimation data is available
      if (details?.costEstimation?.roles && details.costEstimation.roles.length > 0) {
        details.costEstimation.roles.forEach(role => {
          // Calculate role details
          const rosterMember = itRoster.find(member => member.id === role.roster_member_id);
          const company = rosterMember?.company || role.company || 'Unknown';
          const location = rosterMember?.location || role.location || 'Unknown';
          const roleFunction = rosterMember?.function || role.function || role.name || 'Unknown';
          const roleName = role.name || role.function || 'Unknown Role';
          const rate = role.rate || 0;
          
          // Process each date range for the role
          if (role.dateRanges && role.dateRanges.length > 0) {
            role.dateRanges.forEach(dateRange => {
              const startMonth = dateRange.startMonth || 1;
              const endMonth = dateRange.endMonth || details.costEstimation.adjustedMonths || 6;
              const allocation = dateRange.allocation || 100;
              const duration = endMonth - startMonth + 1;
              const totalCost = rate * (duration * (allocation / 100));
              
              const roleRow = [
                '', '', '', '', '', '', '',
                company, location, roleFunction, roleName, rate, allocation,
                startMonth, endMonth, duration, totalCost,
                role.capex_classification || 'OPEX'
              ];
              data.push(roleRow);
            });
          } else {
            // Fallback for roles without date ranges
            const duration = details.costEstimation.adjustedMonths || 6;
            const totalCost = rate * duration;
            
            const roleRow = [
              '', '', '', '', '', '', '',
              company, location, roleFunction, roleName, rate, 100,
              1, duration, duration, totalCost,
              role.capex_classification || 'OPEX'
            ];
            data.push(roleRow);
          }
        });
      }
      
      // Add fixed costs if available
      if (details?.costEstimation?.fixedCosts && details.costEstimation.fixedCosts.length > 0) {
        details.costEstimation.fixedCosts.forEach(fixedCost => {
          const duration = fixedCost.duration || details.costEstimation.adjustedMonths || 6;
          const totalCost = (fixedCost.monthlyCost || 0) * duration;
          
          const fixedCostRow = [
            '', '', '', '', '', '', '',
            'CA', 'Various', 'Fixed Cost', fixedCost.description || 'Fixed Cost',
            fixedCost.monthlyCost || 0, 100, 1, duration, duration, totalCost, 'OPEX'
          ];
          data.push(fixedCostRow);
        });
      }
    });

    return data;
  }, [formData.selected_outcomes, formData.outcome_order, formData.plan_statuses, availableOutcomes, calculateOutcomeCapexOpex, outcomeDetails, itRoster]);

    // Excel export functionality
  const exportToExcel = useCallback(() => {
    if (!exportData) {
      alert('No outcomes to export');
      return;
    }

    const finalExportData = [...exportData];
    
        // Add plan summary at the end
    finalExportData.push([]);
    finalExportData.push([
      'PLAN SUMMARY',
      '',
      '',
      calculateTotalBudget,
      calculateTotalCapexOpex.totalCapex,
      calculateTotalCapexOpex.totalOpex,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'Plan Total'
    ]);
    
        
    // Add plan metadata
    finalExportData.push([]);
    finalExportData.push(['Plan Name:', formData.name]);
    finalExportData.push(['Description:', formData.description]);
    finalExportData.push(['Year:', formData.year]);
    finalExportData.push(['Status:', formData.status]);
    finalExportData.push(['Target Budget:', formData.target_budget ? `$${formData.target_budget}` : 'Not set']);
    finalExportData.push(['Total Outcomes:', formData.selected_outcomes.length]);
    finalExportData.push(['Approved Outcomes:', formData.selected_outcomes.filter(id => formData.plan_statuses[id] === 'Approved').length]);
    finalExportData.push(['Declined Outcomes:', formData.selected_outcomes.filter(id => formData.plan_statuses[id] === 'Declined').length]);
    
        
    // Add break-even and savings data if available
    if (planBreakEven) {
      finalExportData.push([]);
      finalExportData.push(['BREAK-EVEN ANALYSIS']);
      finalExportData.push(['Simple Break-even (months):', planBreakEven.simpleBreakEvenMonths.toFixed(1)]);
      finalExportData.push(['Weighted Break-even (months):', planBreakEven.weightedBreakEvenMonths.toFixed(1)]);
      finalExportData.push(['Total Development Cost:', planBreakEven.totalDevelopmentCost]);
      finalExportData.push(['Total Annual Savings:', planBreakEven.totalAnnualSavings]);
    }
    
    if (planSavings && planSavings.contributingOutcomes > 0) {
      finalExportData.push([]);
      finalExportData.push(['SAVINGS ANALYSIS']);
      finalExportData.push(['Total 1-Year Savings:', planSavings.totalSavings]);
      finalExportData.push(['Savings Percentage:', `${planSavings.savingsPercentage.toFixed(1)}%`]);
      finalExportData.push(['Contributing Outcomes:', planSavings.contributingOutcomes]);
      finalExportData.push(['Total Outcomes:', planSavings.totalOutcomes]);
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(finalExportData);
    
    // Set column widths
    const colWidths = [
      { wch: 30 }, // Outcome Name
      { wch: 15 }, // Outcome Status
      { wch: 15 }, // Plan Status
      { wch: 15 }, // Total Budget
      { wch: 12 }, // CAPEX
      { wch: 12 }, // OPEX
      { wch: 12 }, // Budget Source
      { wch: 12 }, // Company
      { wch: 12 }, // Location
      { wch: 15 }, // Function
      { wch: 20 }, // Role
      { wch: 12 }, // Rate
      { wch: 12 }, // Allocation %
      { wch: 12 }, // Start Month
      { wch: 12 }, // End Month
      { wch: 15 }, // Duration
      { wch: 15 }, // Total Cost
      { wch: 15 }  // CAPEX/OPEX Classification
    ];
    
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Plan Details');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const planName = formData.name ? formData.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Plan';
    const filename = `${planName}_Plan_Export_${formData.year}_${timestamp}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, filename);
    
        // Plan data exported to Excel
  }, [exportData, formData.name, formData.description, formData.year, formData.status, formData.target_budget, formData.selected_outcomes, formData.plan_statuses, calculateTotalBudget, calculateTotalCapexOpex, planBreakEven, planSavings]);

    const { totalCapex, totalOpex } = calculateTotalCapexOpex;
  const { totalOutcomes, opexOnlyOutcomes } = calculateOutcomeStats;

    return (
    <Box>
            {/* Performance Monitor removed for better performance */}
      
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={onClose} sx={{ mr: 2 }}>
          Plans
        </Button>
        <Typography variant="h4">
          {isEditing ? 'Edit Plan' : 'Create New Plan'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Plan Details */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Plan Details</Typography>
              <Chip
                label={formData.status || 'Draft'}
                color={getStatusColor(formData.status)}
                size="small"
                variant="outlined"
              />
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Plan Status Management */}
                            <StyledFormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Plan Status</InputLabel>
                <Select
                  value={formData.status || 'Draft'}
                  label="Plan Status"
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setFormData(prev => ({ ...prev, status: newStatus }));
                    if (newStatus === 'Approved') {
                      handlePlanStatusUpdate(newStatus);
                    }
                  }}
                >
                  {PLAN_STATUSES.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </StyledFormControl>
              
                            <Box sx={{ display: 'flex', gap: 1 }}>
                {formData.selected_outcomes.length > 0 && (
                  <>
                                        <ActionButton
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportToExcel}
                      size="small"
                      disabled={formData.selected_outcomes.length === 0}
                    >
                      Export to Excel
                    </ActionButton>
                                        <ActionButton
                      variant="outlined"
                      startIcon={<PersonIcon />}
                      onClick={() => setRoleDetailsModalOpen(true)}
                      size="small"
                    >
                      Role Details
                    </ActionButton>
                  </>
                )}
              </Box>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Plan Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <NumberTextField
                label="Target Budget"
                type="number"
                value={formData.target_budget}
                onChange={(e) => setFormData(prev => ({ ...prev, target_budget: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
                        <Grid item xs={12} md={3}>
              <StyledFormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.year}
                  label="Year"
                  onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                >
                  <MenuItem value={2025}>2025</MenuItem>
                  <MenuItem value={2026}>2026</MenuItem>
                  <MenuItem value={2027}>2027</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Budget Summary */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={3}>
            {/* Total Budget - Most Important */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoneyIcon 
                  sx={{ 
                    mr: 1, 
                    color: theme.palette.primary.main,
                    fontSize: '1.2rem'
                  }} 
                />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Total Budget
                </Typography>
              </Box>
              <Typography 
                                  variant="h4" 
                  fontWeight={700}
                  color="primary.main"
                  sx={{ lineHeight: 1.2, mb: 1 }}
                >
                  {formatCurrency(calculateTotalBudget)}
                </Typography>
              {/* CAPEX/OPEX breakdown */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="success.main" fontWeight={500}>
                    CAPEX
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    {formatCurrency(totalCapex)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="warning.main" fontWeight={500}>
                    OPEX
                  </Typography>
                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                    {formatCurrency(totalOpex)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

                        {/* Budget Status - Second Most Important */}
            <Grid item xs={12} md={3}>
              {getBudgetStatus ? (
                <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getBudgetStatus.isOver ? (
                      <TrendingUpIcon 
                        sx={{ 
                          mr: 1, 
                          color: theme.palette.error.main,
                          fontSize: '1.2rem'
                        }} 
                      />
                    ) : (
                      <TrendingDownIcon 
                        sx={{ 
                          mr: 1, 
                          color: theme.palette.success.main,
                          fontSize: '1.2rem'
                        }} 
                      />
                    )}
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      vs Target Budget
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                                              <Typography 
                          variant="h6" 
                          fontWeight={600}
                          color={getBudgetStatus.isOver ? 'error.main' : 'success.main'}
                          sx={{ lineHeight: 1.2, mb: 0.5 }}
                        >
                          {getBudgetStatus.text}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color={getBudgetStatus.isOver ? 'error.main' : 'success.main'}
                          fontWeight={500}
                          sx={{ mb: 1 }}
                        >
                          {formatCurrency(getBudgetStatus.difference)}
                        </Typography>
                      <Box sx={{ mt: 1 }}>
                                                  <LinearProgress
                            variant="determinate"
                            value={Math.min(getBudgetStatus.percentage, 100)}
                            color={getBudgetStatus.isOver ? 'error' : 'success'}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: alpha(
                                getBudgetStatus.isOver ? theme.palette.error.main : theme.palette.success.main, 
                                0.2
                              )
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {Math.round(getBudgetStatus.percentage)}% {getBudgetStatus.isOver ? 'over' : 'under'}
                          </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BudgetIcon 
                      sx={{ 
                        mr: 1, 
                        color: theme.palette.text.secondary,
                        fontSize: '1.2rem'
                      }} 
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Target Budget
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={600}
                    color="text.primary"
                    sx={{ lineHeight: 1.2, mb: 1 }}
                  >
                    {formData.target_budget ? formatCurrency(formData.target_budget) : 'Not set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set target to see variance
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Outcome Statistics */}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssessmentIcon 
                  sx={{ 
                    mr: 1, 
                    color: theme.palette.info.main,
                    fontSize: '1.2rem'
                  }} 
                />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Outcomes
                </Typography>
              </Box>
              <Typography 
                variant="h5" 
                fontWeight={600}
                color="info.main"
                sx={{ lineHeight: 1.2, mb: 0.5 }}
              >
                {totalOutcomes}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Active outcomes
              </Typography>
              {opexOnlyOutcomes > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.main" fontWeight={500}>
                    {opexOnlyOutcomes} OPEX only
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Break-even Analysis */}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon 
                  sx={{ 
                    mr: 1, 
                    color: theme.palette.info.main,
                    fontSize: '1.2rem'
                  }} 
                />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Break-even
                </Typography>
              </Box>
              {planBreakEven ? (
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={600}
                    color="info.main"
                    sx={{ lineHeight: 1.2, mb: 0.5 }}
                  >
                    {planBreakEven.simpleBreakEvenMonths.toFixed(1)} months
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Simple: {formatCurrency(planBreakEven.totalDevelopmentCost)} ÷ {formatCurrency(planBreakEven.totalAnnualSavings)}/yr
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                  >
                    Weighted avg: {planBreakEven.weightedBreakEvenMonths.toFixed(1)} months
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No financial data available
                </Typography>
              )}
            </Grid>

            {/* 1-Year Savings */}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDownIcon 
                  sx={{ 
                    mr: 1, 
                    color: theme.palette.success.main,
                    fontSize: '1.2rem'
                  }} 
                />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  1-Year Savings
                </Typography>
              </Box>
              {planSavings && planSavings.contributingOutcomes > 0 ? (
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={600}
                    color="success.main"
                    sx={{ lineHeight: 1.2, mb: 0.5 }}
                  >
                    {formatCurrency(planSavings.totalSavings)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="success.main"
                    fontWeight={500}
                    sx={{ mb: 1 }}
                  >
                    {planSavings.savingsPercentage.toFixed(1)}% savings
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                  >
                    {planSavings.contributingOutcomes} of {planSavings.totalOutcomes} outcomes contributing
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" color="text.secondary">
                    No savings data available
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                  >
                    Add roles & efficiency gains to outcomes
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Year Badge - Bottom Right */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Chip
              label={`Year ${formData.year}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Select Outcomes - Collapsible */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader 
          title="Select Outcomes"
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!outcomeSelectionExpanded && (
                <Typography variant="body2" color="text.secondary">
                  {formData.selected_outcomes.length} of {availableOutcomes.length} selected
                </Typography>
              )}
              <IconButton
                onClick={() => setOutcomeSelectionExpanded(!outcomeSelectionExpanded)}
                aria-label={outcomeSelectionExpanded ? 'collapse outcome selection' : 'expand outcome selection'}
              >
                {outcomeSelectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
                <Collapse in={outcomeSelectionExpanded}>
          <Box sx={{ p: 3, pt: 0 }}>
                        <Suspense fallback={
              <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, i) => (
                  <ComponentSkeleton key={i} height={60} />
                ))}
              </Box>
            }>
              <OutcomeSelectionTable
                availableOutcomes={availableOutcomes}
                selectedOutcomes={formData.selected_outcomes}
                onSelectionChange={handleOutcomeSelectionChange}
                outcomeDetails={outcomeDetails}
                calculateOutcomeBudget={calculateOutcomeBudget}
                onViewDetails={openDetailsModal}
              />
            </Suspense>
          </Box>
        </Collapse>
      </Card>

      {/* Plan Outcomes - Expanded by default */}
      {formData.selected_outcomes.length > 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardHeader 
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  Plan Outcomes (                {getPlanOutcomeCounts.totalCount})
                </Typography>
                
                                                {/* Plan Status Filter */}
                <StyledFormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Plan Status</InputLabel>
                  <Select
                    multiple
                    value={planStatusFilter}
                    onChange={handlePlanStatusFilterChange}
                    label="Filter by Plan Status"
                    renderValue={(selected) => {
                      if (selected.includes('All')) {
                        return 'All Statuses';
                      }
                      if (selected.length === 0) return '';
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} selected`;
                    }}
                  >
                    <MenuItem value="All">
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: '2px solid',
                            borderColor: planStatusFilter.includes('All') ? 'primary.main' : 'grey.400',
                            backgroundColor: planStatusFilter.includes('All') ? 'primary.main' : 'transparent',
                            borderRadius: '3px',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {planStatusFilter.includes('All') && (
                            <Box sx={{ color: 'white', fontSize: '12px', lineHeight: 1 }}>✓</Box>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          All Statuses
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Approved">
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: '2px solid',
                            borderColor: planStatusFilter.includes('Approved') ? 'primary.main' : 'grey.400',
                            backgroundColor: planStatusFilter.includes('Approved') ? 'primary.main' : 'transparent',
                            borderRadius: '3px',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {planStatusFilter.includes('Approved') && (
                            <Box sx={{ color: 'white', fontSize: '12px', lineHeight: 1 }}>✓</Box>
                          )}
                        </Box>
                        Approved
                      </Box>
                    </MenuItem>
                    <MenuItem value="Under Consideration">
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: '2px solid',
                            borderColor: planStatusFilter.includes('Under Consideration') ? 'primary.main' : 'grey.400',
                            backgroundColor: planStatusFilter.includes('Under Consideration') ? 'primary.main' : 'transparent',
                            borderRadius: '3px',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {planStatusFilter.includes('Under Consideration') && (
                            <Box sx={{ color: 'white', fontSize: '12px', lineHeight: 1 }}>✓</Box>
                          )}
                        </Box>
                        Under Consideration
                      </Box>
                    </MenuItem>
                    <MenuItem value="Declined">
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: '2px solid',
                            borderColor: planStatusFilter.includes('Declined') ? 'primary.main' : 'grey.400',
                            backgroundColor: planStatusFilter.includes('Declined') ? 'primary.main' : 'transparent',
                            borderRadius: '3px',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {planStatusFilter.includes('Declined') && (
                            <Box sx={{ color: 'white', fontSize: '12px', lineHeight: 1 }}>✓</Box>
                          )}
                        </Box>
                        Declined
                      </Box>
                    </MenuItem>
                                    </Select>
                </StyledFormControl>

                {/* Department Filter */}
                {departments.length > 0 && (
                  <StyledFormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={deptFilter}
                      onChange={(e) => setDeptFilter(e.target.value)}
                      label="Department"
                    >
                      <MenuItem value=""><em>All Departments</em></MenuItem>
                      {departments.map(d => (
                        <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                )}

                {/* Filter indicator chip */}
                {!planStatusFilter.includes('All') && (
                  <Chip
                    label={`${getPlanOutcomeCounts.totalCount} of ${getPlanOutcomeCounts.allOutcomesCount} shown`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            }
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!planOutcomesExpanded && (
                  <Typography variant="body2" color="text.secondary">
                                        {getPlanOutcomeCounts.approvedCount} of {getPlanOutcomeCounts.totalCount} approved
                    {!planStatusFilter.includes('All') && ` (filtered)`}
                  </Typography>
                )}
                                <IconButton
                  onClick={() => setPlanOutcomesExpanded(!planOutcomesExpanded)}
                  aria-label={planOutcomesExpanded ? 'collapse plan outcomes' : 'expand plan outcomes'}
                >
                  {planOutcomesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            }
          />
          <Collapse in={planOutcomesExpanded}>
            <Box sx={{ p: 3, pt: 0 }}>
              {getFilteredOrderedOutcomes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {planStatusFilter.includes('All') 
                      ? 'No outcomes selected for this plan'
                      : `No outcomes match the selected filter: ${planStatusFilter.join(', ')}`
                    }
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {getFilteredOrderedOutcomes.map((outcome, index) => {
                    const planStatus = formData.plan_statuses[outcome.id] || "Under Consideration";
                    const isDeclined = planStatus === "Declined";
                    const capexOpexResult = outcomeCapexOpexMap[outcome.id]?.adjusted || { capex: 0, opex: 0 };
                    const totalBudget = capexOpexResult.capex + capexOpexResult.opex;
                    const originalCapexOpex = outcomeCapexOpexMap[outcome.id]?.base || { capex: 0, opex: 0 };

                    const deptBudgets = outcome.department_budgets || [];
                    const filteredDeptBudgets = deptFilter
                      ? deptBudgets.filter(db => db.department_name === deptFilter)
                      : deptBudgets;

                    return (
                      <Box key={outcome.id}>
                        <Suspense fallback={<OutcomeCardSkeleton />}>
                          <OutcomeCard
                            outcome={outcome}
                            index={index}
                            planStatus={planStatus}
                            isDeclined={isDeclined}
                            capexOpexResult={capexOpexResult}
                            totalBudget={totalBudget}
                            originalCapexOpex={originalCapexOpex}
                            formData={formData}
                            getFilteredOrderedOutcomes={getFilteredOrderedOutcomes}
                            moveOutcome={moveOutcome}
                            handleOutcomeStatusChange={handleOutcomeStatusChange}
                            handlePlanStatusChange={handlePlanStatusChange}
                            handleCapexAdjustmentChange={handleCapexAdjustmentChange}
                            handleOpexAdjustmentChange={handleOpexAdjustmentChange}
                            handleSyncToggle={handleSyncToggle}
                            openDetailsModal={openDetailsModal}
                            formatCurrency={formatCurrency}
                            allowedStatuses={allowedStatuses}
                            planStatuses={planStatuses}
                          />
                        </Suspense>
                        {filteredDeptBudgets.length > 0 && (
                          <Box sx={{ mx: 2, mb: 2, mt: -1 }}>
                            <Box sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              overflow: 'hidden',
                              backgroundColor: 'background.paper'
                            }}>
                              <Box sx={{ px: 2, py: 0.75, backgroundColor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                  {deptFilter ? `${deptFilter} Budget` : 'Department Budgets'}
                                </Typography>
                              </Box>
                              {filteredDeptBudgets.map((db) => (
                                <Box key={db.department_id || db.department_name} sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
                                    {db.department_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    C: {formatCurrency(db.capex || 0)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    O: {formatCurrency(db.opex || 0)}
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600}>
                                    {formatCurrency((db.capex || 0) + (db.opex || 0))}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Collapse>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <BackButton onClick={onClose}>
          Cancel
        </BackButton>
        <ActionButton
          onClick={handleSave}
          disabled={loading || !formData.name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update Plan' : 'Create Plan')}
        </ActionButton>
      </Box>

      {/* Modals */}
      <Suspense fallback={<div>Loading...</div>}>
        <OutcomeDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          outcome={selectedOutcomeForDetails}
          outcomeDetails={selectedOutcomeForDetails ? outcomeDetails[selectedOutcomeForDetails.id] : null}
          planStatus={selectedOutcomeForDetails ? formData.plan_statuses[selectedOutcomeForDetails.id] : null}
          onPlanStatusChange={handleModalPlanStatusChange}
          onOutcomeStatusChange={handleModalOutcomeStatusChange}
          onViewFinancialDetails={handleViewFinancialDetails}
          allowedStatuses={allowedStatuses}
          planStatuses={planStatuses}
          formatCurrency={formatCurrency}
        />
      </Suspense>

      <Suspense fallback={<div>Loading...</div>}>
        <RoleDetailsModal
          open={roleDetailsModalOpen}
          onClose={() => setRoleDetailsModalOpen(false)}
          outcomes={getFilteredOrderedOutcomes}
          outcomeDetails={outcomeDetails}
          itRoster={itRoster}
          getFilteredRosterForRole={getFilteredRosterForRole}
          formData={formData}
          formatCurrency={formatCurrency}
        />
      </Suspense>
    </Box>
  );
});

export default CreateEditPlan; 